import { useState, useEffect, useCallback, useRef } from 'react';
import { rrhhApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import { UtensilsCrossed, Users, ChevronDown, ChevronUp, Download, X, Calendar } from 'lucide-react';
import axios from 'axios';

const TIPO_COLORS = {
  A: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  H: 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
  C: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
};

function CasinoGrid({ section, daysInMonth, dayLabels }) {
  const [open, setOpen] = useState(true);
  const s = section;
  // Iter 236.v.4 (Renzo): usar day_labels del backend cuando exista (para
  // rango quincenal). Fallback a 1..N para retro-compatibilidad.
  const labels = (dayLabels && dayLabels.length === daysInMonth)
    ? dayLabels
    : Array.from({ length: daysInMonth }, (_, i) => String(i + 1));

  return (
    <Card data-testid={`casino-section-${s.casino_label}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors rounded-t-xl"
        data-testid={`casino-toggle-${s.casino_label}`}
      >
        <div className="flex items-center gap-3 flex-1">
          <UtensilsCrossed className="h-4 w-4 text-primary shrink-0" />
          <div className="flex flex-col items-start gap-0.5">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-sm">{s.casino_label}</span>
              <span className="text-xs text-muted-foreground">
                {s.total_trabajadores} personas · {s.total_almuerzos} almuerzos
              </span>
              <div className="flex items-center gap-1.5">
                {s.type_totals?.A > 0 && <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.A}`}>A:{s.type_totals.A}</span>}
                {s.type_totals?.H > 0 && <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.H}`}>H:{s.type_totals.H}</span>}
                {s.type_totals?.C > 0 && <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.C}`}>C:{s.type_totals.C}</span>}
              </div>
            </div>
            {/* Iter 236.v — badge proveedor Alegra (readonly) */}
            {s.proveedor_rut && (
              <div className="flex items-center gap-1.5 text-[11px] text-blue-700 dark:text-blue-300"
                   data-testid={`casino-proveedor-badge-${s.casino_label}`}>
                <span className="font-mono bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded px-1.5 py-0.5">
                  RUT {s.proveedor_rut}
                </span>
                <span className="text-muted-foreground">·</span>
                <span className="truncate max-w-[300px]">{s.proveedor_razon_social || '—'}</span>
              </div>
            )}
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <CardContent className="p-0 border-t">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="sticky left-0 bg-muted/80 backdrop-blur z-10 px-2 py-1.5 text-left font-medium min-w-[140px] border-r">Trabajador</th>
                  {labels.map((lbl, i) => (
                    <th key={i} className="px-0.5 py-1.5 text-center font-medium w-7 min-w-[28px]">{lbl}</th>
                  ))}
                  <th className="px-2 py-1.5 text-center font-bold border-l min-w-[36px]">Tot</th>
                </tr>
              </thead>
              <tbody>
                {s.grid.length === 0 ? (
                  <tr><td colSpan={daysInMonth + 2} className="text-center py-6 text-muted-foreground">Sin datos</td></tr>
                ) : (
                  <>
                    {s.grid.map((row, ri) => (
                      <tr key={row.nombre || `row-${ri}`} className="border-t hover:bg-muted/20 transition-colors">
                        <td className="sticky left-0 bg-background/95 backdrop-blur z-10 px-2 py-1 font-medium border-r truncate max-w-[140px]" title={row.nombre}>
                          {row.nombre}
                        </td>
                        {row.cells.map((cell, ci) => (
                          <td key={ci} className="px-0.5 py-0.5 text-center">
                            {cell ? (
                              <span className={`inline-block w-5 h-5 leading-5 rounded text-[10px] font-bold ${TIPO_COLORS[cell] || TIPO_COLORS.A}`}>{cell}</span>
                            ) : null}
                          </td>
                        ))}
                        <td className="px-2 py-1 text-center font-bold border-l tabular-nums">{row.total}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 bg-muted/30 font-bold">
                      <td className="sticky left-0 bg-muted/80 backdrop-blur z-10 px-2 py-1.5 border-r">TOTAL</td>
                      {(s.day_totals || []).map((t, i) => (
                        <td key={i} className="px-0.5 py-1.5 text-center tabular-nums">{t || ''}</td>
                      ))}
                      <td className="px-2 py-1.5 text-center tabular-nums border-l">{s.total_almuerzos}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function ResumenCasino() {
  const now = new Date();
  const [mes, setMes] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [lugar, setLugar] = useState('');
  // Iter 236.v — modo de agrupación
  const [agruparPor, setAgruparPor] = useState('casino');
  // Iter 236.v.4 (Renzo, feb-2026) — rango custom (útil para quincenas).
  // `desde`/`hasta` en formato YYYY-MM-DD. Se aplican solo si están dentro del
  // mismo mes que `mes`. `rangoPickerOpen` controla el popover.
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [rangoPickerOpen, setRangoPickerOpen] = useState(false);
  const [pickerDesde, setPickerDesde] = useState('');
  const [pickerHasta, setPickerHasta] = useState('');
  const rangoPopoverRef = useRef(null);
  const rangoActivo = Boolean(desde && hasta);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await rrhhApi.getResumenCasino({
        mes,
        lugar: lugar || undefined,
        agrupar_por: agruparPor,
        desde: rangoActivo ? desde : undefined,
        hasta: rangoActivo ? hasta : undefined,
      });
      setData(res.data);
    } catch {
      toast({ title: 'Error al cargar resumen', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [mes, lugar, agruparPor, desde, hasta, rangoActivo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Iter 236.v.4 — cerrar popover al hacer click fuera
  useEffect(() => {
    if (!rangoPickerOpen) return;
    const onClickOutside = (e) => {
      if (rangoPopoverRef.current && !rangoPopoverRef.current.contains(e.target)) {
        setRangoPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [rangoPickerOpen]);

  // Iter 236.v.4 — al abrir el picker, precargar con el rango actual (o primer/último día del mes)
  const abrirRangoPicker = () => {
    if (desde && hasta) {
      setPickerDesde(desde);
      setPickerHasta(hasta);
    } else {
      // Default: primer y último día del mes seleccionado
      const [y, m] = mes.split('-').map(Number);
      const primerDia = `${mes}-01`;
      const ultimoDia = new Date(y, m, 0).getDate();
      setPickerDesde(primerDia);
      setPickerHasta(`${mes}-${String(ultimoDia).padStart(2, '0')}`);
    }
    setRangoPickerOpen(true);
  };

  const aplicarRango = () => {
    // Validar que ambos estén dentro del mismo mes que `mes`
    if (!pickerDesde || !pickerHasta) {
      toast({ title: 'Selecciona ambas fechas', variant: 'destructive' });
      return;
    }
    if (pickerDesde > pickerHasta) {
      toast({ title: 'La fecha "desde" no puede ser mayor a "hasta"', variant: 'destructive' });
      return;
    }
    if (pickerDesde.substring(0, 7) !== mes || pickerHasta.substring(0, 7) !== mes) {
      toast({ title: 'El rango debe estar dentro del mes seleccionado', variant: 'destructive' });
      return;
    }
    setDesde(pickerDesde);
    setHasta(pickerHasta);
    setRangoPickerOpen(false);
  };

  const limpiarRango = () => {
    setDesde('');
    setHasta('');
    setPickerDesde('');
    setPickerHasta('');
    setRangoPickerOpen(false);
  };

  // Iter 236.v.4 — helper para formatear el badge del rango activo (DD-DD MMM)
  const fmtRangoBadge = () => {
    if (!rangoActivo) return '';
    const d = parseInt(desde.split('-')[2]);
    const h = parseInt(hasta.split('-')[2]);
    const [y, m] = mes.split('-').map(Number);
    const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${d}-${h} ${meses[m - 1]} ${y}`;
  };

  if (loading && !data) return <TableSkeleton rows={10} cols={15} />;

  const d = data || { days_in_month: 30, casino_sections: [], type_totals: {}, lugares_disponibles: [] };
  const sections = d.casino_sections || [];

  return (
    <div className="space-y-4 page-enter" data-testid="resumen-casino">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Resumen por Casino</h1>
          <p className="text-sm text-muted-foreground" data-testid="resumen-casino-subtitulo">
            {rangoActivo
              ? <>Rango <strong className="text-blue-700 dark:text-blue-400">{fmtRangoBadge()}</strong> · {d.days_in_month} día(s)</>
              : 'Cuadrícula mensual separada por casino/lugar · Doble-click al mes para elegir un rango custom (ej. quincena)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Iter 236.v — selector Agrupar Por */}
          <select
            className="h-9 rounded-xl border bg-background px-3 text-sm"
            value={agruparPor}
            onChange={(e) => setAgruparPor(e.target.value)}
            data-testid="select-agrupar-por"
            title="Agrupar por Casino individual o por RUT del proveedor"
          >
            <option value="casino">Agrupar por Casino</option>
            <option value="rut">Agrupar por RUT</option>
          </select>
          <select
            className="h-9 rounded-xl border bg-background px-3 text-sm"
            value={lugar}
            onChange={(e) => setLugar(e.target.value)}
            data-testid="select-lugar-filtro"
          >
            <option value="">Todos los lugares</option>
            <option value="casino_cliente">Casino Cliente</option>
            <option value="sitio_escuadron">Sitio Escuadrón</option>
            <option value="maestranza">Maestranza</option>
            <option value="otro">Otro</option>
            {(d.lugares_disponibles || []).filter(l => !['casino_cliente', 'sitio_escuadron', 'maestranza', 'otro'].includes(l)).map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <div className="relative flex items-center gap-1" ref={rangoPopoverRef}>
            <input
              type="month"
              value={mes}
              onChange={(e) => { setMes(e.target.value); if (rangoActivo) limpiarRango(); }}
              onDoubleClick={abrirRangoPicker}
              className={`h-9 rounded-xl border bg-background px-3 text-sm cursor-pointer ${rangoActivo ? 'border-blue-400 ring-1 ring-blue-200' : ''}`}
              title="Doble-click (o botón 📅) para elegir un rango de días (ej. quincena)"
              data-testid="input-mes"
            />
            {/* Iter 236.v.4 — botón explícito para descubribilidad (sugerido por testing agent) */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`h-9 w-9 p-0 ${rangoActivo ? 'border-blue-400 bg-blue-50 text-blue-700' : ''}`}
              onClick={abrirRangoPicker}
              title={rangoActivo ? `Rango activo: ${fmtRangoBadge()}` : 'Elegir rango de días (quincena, semana)'}
              data-testid="btn-abrir-rango-picker"
            >
              <Calendar className="h-4 w-4" />
            </Button>
            {rangoActivo && (
              <span
                className="ml-2 inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-2 py-0.5 align-middle"
                data-testid="badge-rango-activo"
              >
                <Calendar className="h-3 w-3" />
                {fmtRangoBadge()}
                <button
                  onClick={limpiarRango}
                  className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                  title="Limpiar rango (volver al mes completo)"
                  data-testid="btn-limpiar-rango"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {rangoPickerOpen && (
              <div
                className="absolute right-0 top-11 z-50 w-72 rounded-xl border bg-background shadow-lg p-3 space-y-2"
                data-testid="rango-picker-popover"
              >
                <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Rango custom dentro de {mes}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-muted-foreground">Desde</label>
                    <input
                      type="date"
                      value={pickerDesde}
                      onChange={(e) => setPickerDesde(e.target.value)}
                      min={`${mes}-01`}
                      max={`${mes}-31`}
                      className="w-full h-8 rounded border bg-background px-2 text-xs"
                      data-testid="picker-desde"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">Hasta</label>
                    <input
                      type="date"
                      value={pickerHasta}
                      onChange={(e) => setPickerHasta(e.target.value)}
                      min={`${mes}-01`}
                      max={`${mes}-31`}
                      className="w-full h-8 rounded border bg-background px-2 text-xs"
                      data-testid="picker-hasta"
                    />
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Ej. quincena: <em>01-15</em> o <em>16-{new Date(parseInt(mes.split('-')[0]), parseInt(mes.split('-')[1]), 0).getDate()}</em>. Fuera del mes se ignora.
                </div>
                <div className="flex justify-between gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={limpiarRango}
                    disabled={!rangoActivo}
                    className="h-7 text-xs"
                    data-testid="btn-picker-limpiar"
                  >
                    Mes completo
                  </Button>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRangoPickerOpen(false)}
                      className="h-7 text-xs"
                      data-testid="btn-picker-cancelar"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={aplicarRango}
                      className="h-7 text-xs"
                      data-testid="btn-picker-aplicar"
                    >
                      Aplicar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Iter 236.q (Carlos): exportar Excel */}
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                // Iter 236.v.4 (Renzo, feb-2026): incluir rango custom en el Excel
                const rangoQP = rangoActivo ? `&desde=${desde}&hasta=${hasta}` : '';
                const url = `${window.location.origin}/api/rrhh/resumen-casino/export-excel?mes=${mes}&agrupar_por=${agruparPor}${lugar ? `&lugar=${lugar}` : ''}${rangoQP}`;
                const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = blobUrl;
                const rangoSuffix = rangoActivo ? `_${desde.substring(8)}-${hasta.substring(8)}` : '';
                a.download = `resumen_casino_${mes}${rangoSuffix}${agruparPor === 'rut' ? '_por_rut' : ''}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
                toast({ title: `Excel descargado (${agruparPor === 'rut' ? 'por RUT' : 'por Casino'}${rangoActivo ? ` · ${fmtRangoBadge()}` : ''})` });
              } catch (err) {
                toast({ title: 'Error al exportar', description: err?.message, variant: 'destructive' });
              }
            }}
            data-testid="btn-export-excel-casino"
          >
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Global KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10"><Users className="h-4 w-4 text-primary" /></div>
            <div><p className="text-[10px] text-muted-foreground">Personas</p><p className="text-base font-bold tabular-nums">{d.total_trabajadores}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-500/10"><UtensilsCrossed className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /></div>
            <div><p className="text-[10px] text-muted-foreground">Total</p><p className="text-base font-bold tabular-nums">{d.total_almuerzos}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-500/10"><span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">A</span></div>
            <div><p className="text-[10px] text-muted-foreground">Normal</p><p className="text-base font-bold tabular-nums">{d.type_totals?.A || 0}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-amber-500/10"><span className="text-xs font-bold text-amber-700 dark:text-amber-300">H</span></div>
            <div><p className="text-[10px] text-muted-foreground">Hipocalórico</p><p className="text-base font-bold tabular-nums">{d.type_totals?.H || 0}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-blue-500/10"><span className="text-xs font-bold text-blue-700 dark:text-blue-300">C</span></div>
            <div><p className="text-[10px] text-muted-foreground">Colación</p><p className="text-base font-bold tabular-nums">{d.type_totals?.C || 0}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Casino sections */}
      {sections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Sin datos de almuerzos para este periodo
          </CardContent>
        </Card>
      ) : agruparPor === 'rut' && Array.isArray(d.rut_groups) ? (
        // Iter 236.v — vista agrupada por RUT del proveedor
        <div className="space-y-4" data-testid="rut-groups-container">
          {d.rut_groups.map((g, gi) => (
            <div key={g.proveedor_rut || `sin-rut-${gi}`}
                 className="rounded-2xl border-2 border-blue-200/60 dark:border-blue-800/50 bg-blue-50/30 dark:bg-blue-950/10 p-3 space-y-2"
                 data-testid={`rut-group-${g.proveedor_rut || 'sin-rut'}`}>
              <div className="flex items-center justify-between px-2 py-1">
                <div className="flex items-center gap-2">
                  {g.sin_rut ? (
                    <span className="text-sm font-semibold text-amber-700">
                      Sin proveedor vinculado
                    </span>
                  ) : (
                    <>
                      <span className="font-mono text-sm font-semibold bg-blue-100 dark:bg-blue-900/40 border border-blue-300 dark:border-blue-700 rounded px-2 py-0.5">
                        {g.proveedor_rut}
                      </span>
                      <span className="text-sm font-medium">
                        {g.proveedor_razon_social || '—'}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{g.casinos.length} {g.casinos.length === 1 ? 'casino' : 'casinos'}</span>
                  <span>·</span>
                  <span>{g.total_trabajadores} personas</span>
                  <span>·</span>
                  <span className="font-semibold">{g.total_almuerzos} almuerzos</span>
                  {g.type_totals?.A > 0 && <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.A}`}>A:{g.type_totals.A}</span>}
                  {g.type_totals?.H > 0 && <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.H}`}>H:{g.type_totals.H}</span>}
                  {g.type_totals?.C > 0 && <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${TIPO_COLORS.C}`}>C:{g.type_totals.C}</span>}
                </div>
              </div>
              <div className="space-y-2">
                {g.casinos.map((s) => (
                  <CasinoGrid key={s.casino_label} section={s} daysInMonth={d.days_in_month} dayLabels={d.day_labels} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid="casino-sections-container">
          {sections.map((s) => (
            <CasinoGrid key={s.casino_label} section={s} daysInMonth={d.days_in_month} dayLabels={d.day_labels} />
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className={`inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ${TIPO_COLORS.A}`}>A</span> Normal</span>
        <span className="flex items-center gap-1"><span className={`inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ${TIPO_COLORS.H}`}>H</span> Hipocalórico</span>
        <span className="flex items-center gap-1"><span className={`inline-block w-5 h-5 leading-5 rounded text-center text-[10px] font-bold ${TIPO_COLORS.C}`}>C</span> Colación</span>
      </div>
    </div>
  );
}
